import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Ship, Car } from "lucide-react";
import api from "@/lib/api";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const BookingCalendar = ({ bookings }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [ships, setShips] = useState([]);
  const [selected, setSelected] = useState(iso(new Date()));
  useEffect(() => { api.get("/cruise-ships").then(({ data }) => setShips(data)).catch(() => {}); }, []);

  const byDay = useMemo(() => {
    const m = {};
    bookings.filter((b) => b.status !== "cancelled").forEach((b) => { (m[b.date] = m[b.date] || { rides: [], ships: [] }).rides.push(b); });
    ships.forEach((s) => { if (s.arrival_date) (m[s.arrival_date] = m[s.arrival_date] || { rides: [], ships: [] }).ships.push(s); });
    return m;
  }, [bookings, ships]);

  const cells = [];
  for (let i = 0; i < month.getDay(); i++) cells.push(null);
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  for (let d = 1; d <= days; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  const today = iso(new Date());
  const sel = byDay[selected] || { rides: [], ships: [] };
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="booking-calendar">
      <div className="rounded-sm bg-navy-light border border-gold/20 p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="rounded-full border border-gold/30 p-2 text-gold hover:border-gold"><ChevronLeft className="h-4 w-4" /></button>
          <h2 className="font-heading text-xl font-bold text-white" data-testid="cal-month">{month.toLocaleString("en-CA", { month: "long", year: "numeric" })}</h2>
          <button onClick={() => shift(1)} data-testid="cal-next" className="rounded-full border border-gold/30 p-2 text-gold hover:border-gold"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-wider text-gold/70 mb-1">{WEEK.map((w) => <div key={w}>{w}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const k = iso(d); const info = byDay[k]; const n = info?.rides.length || 0; const sh = info?.ships.length || 0;
            const busy = n >= 4 || (sh > 0 && n >= 2);
            return (
              <button key={k} onClick={() => setSelected(k)} data-testid={`cal-day-${k}`}
                className={`min-h-[68px] rounded-sm border p-1.5 text-left transition-colors duration-150 ${selected === k ? "border-gold bg-gold/10" : busy ? "border-red-500/40 bg-red-500/10" : "border-gold/10 hover:border-gold/40"}`}>
                <div className={`text-xs font-semibold ${k === today ? "text-gold" : "text-slate-300"}`}>{d.getDate()}</div>
                {n > 0 && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-gold/20 px-1.5 text-[10px] font-bold text-gold"><Car className="h-3 w-3" />{n}</div>}
                {sh > 0 && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-sky-500/20 px-1.5 text-[10px] font-bold text-sky-300"><Ship className="h-3 w-3" />{sh}</div>}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-slate-500">Gold = rides booked · Blue = cruise ships in port · Red = busy day</p>
      </div>
      <div className="rounded-sm bg-navy-light border border-gold/20 p-5" data-testid="cal-day-details">
        <h3 className="font-heading text-lg font-bold text-white mb-3">{new Date(selected + "T12:00").toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}</h3>
        {sel.ships.map((s) => (
          <div key={s.id || s.ship_name} className="mb-2 rounded-sm border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-200"><Ship className="inline h-4 w-4 mr-1" />{s.ship_name}{s.arrival_time ? ` · ${s.arrival_time}` : ""}{s.departure_time ? `–${s.departure_time}` : ""}{s.dock ? ` · ${s.dock}` : ""}</div>
        ))}
        {sel.rides.length === 0 ? <p className="text-sm text-slate-500">No rides booked.</p> : sel.rides.map((b) => (
          <div key={b.id} className="border-b border-gold/10 py-2.5 text-sm" data-testid={`cal-ride-${b.id}`}>
            <div className="text-white font-medium">{b.time || "—"} · {b.name}</div>
            <div className="text-slate-400 text-xs">{b.description}</div>
            <div className="text-slate-500 text-xs">{b.pickup}{b.driver_name ? ` · Driver: ${b.driver_name}` : " · No driver yet"}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
